import React, { useState, useEffect } from 'react';
import { showErrorToast, showSuccessToast } from './toast';
import { X, Upload, Save } from 'lucide-react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
const BlogAdmin = () => {
  const [blogs, setBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [formData, setFormData] = useState({
    id: 0,
    title: '',
    slug: '',
    htmlContent: '',
    shortDesc: '',
    metaDescription: '',
    metaKeywords: '',
    blogStatus: 0,
    thumbnailImageUrl: '',
    featureImageUrl: '',
    featureImageAlt: '',
    thumbnailImageAlt: '',
    creatorUserName: 'Admin User'
  });

  const [errors, setErrors] = useState({});

  // Upload state management
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [blogsPerPage] = useState(4);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);


  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [featurePreview, setFeaturePreview] = useState('');

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const toolbarOptions = [
    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image', 'video'],
    ['clean'] // remove formatting button
  ];


  const getStatusBadge = (status) => {
    const intStat = isNaN(parseInt(status)) ? 0 : parseInt(status);
    const statusMap = {
      0: { text: 'Draft', class: 'bg-secondary' },
      1: { text: 'Published', class: 'bg-success' },
      2: { text: 'Archived', class: 'bg-warning' }
    };
    const statusInfo = statusMap[intStat] || { text: 'Unknown', class: 'bg-secondary' };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'title' && { slug: generateSlug(value) })
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  async function imageUrlToBase64(imageUrl) {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();


      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Error converting image to base64:', err);
      showErrorToast("Error converting image to base64");
      return null;
    }
  }


  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.htmlContent.trim()) newErrors.htmlContent = 'Content is required';
    if (!formData.shortDesc.trim()) newErrors.shortDesc = 'Short description is required';
    if (!formData.thumbnailImageUrl?.trim()) newErrors.thumbnailImageUrl = 'Thumbnail image url is required';
    if (!formData.featureImageUrl?.trim()) newErrors.featureImageUrl = 'Feature image url is required';
    if (formData.metaDescription && formData.metaDescription?.length > 160) {
      newErrors.metaDescription = 'Meta description must be 160 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleCreateBlog = async (blog) => {
    setIsUploading(true);
    setUploadProgress('Creating blog...');
    try {
      const createBlogPath = `${process.env.REACT_APP_API_URL}api/Blogs`;
      const token = localStorage.getItem("token");
      const res = await axios.post(createBlogPath, blog, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
   
    } catch (error) {
      setIsUploading(false);
      console.error('❌ Blog upload failed:', error);
      showErrorToast(`Blog upload failed: ${error?.response?.data?.message || error.message}`);
    }
  
  }

  const handleEditBlog = async (blog) => {
    setIsUploading(true);
    setUploadProgress('Editing blog...');
    try {
      const createBlogPath = `${process.env.REACT_APP_API_URL}api/Blogs/${editingBlog.slug}`;
      const token = localStorage.getItem("token");
      const res = await axios.put(createBlogPath, blog, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 60000,
        withCredentials: true,
      });

    } catch (error) {
      setIsUploading(false);
      console.error('❌ Failed to update blog:', error);
      showErrorToast(`Blog update failed: ${error?.response?.data?.message || error.message}`);
    }
  
  }
 
  const getBlogById = async (blog) => {
  

    try {

      const thumbnailUrl = `${process.env.REACT_APP_API_URL}${blog.thumbnailImageUrl?.replace(/^\/+/, '')}`;
      const featureUrl = `${process.env.REACT_APP_API_URL}${blog.featureImageUrl?.replace(/^\/+/, '')}`;

      const thumbnailBase64 = await imageUrlToBase64(thumbnailUrl);
      const featureBase64 = await imageUrlToBase64(featureUrl);

      setThumbnailPreview(thumbnailBase64);
      setFeaturePreview(featureBase64);

      // Set form data with existing values, no promises
      setFormData({
        ...blog,
        thumbnailImageUrl: thumbnailBase64,
        featureImageUrl: featureBase64,
      });

      setEditingBlog(blog);
      setShowModal(true);

    } catch (error) {
      console.error("Error getting blog:", error);
      showErrorToast(`Error getting blog: ${error?.response?.data?.message || error.message}`);
    }
  };



  const deleteBlogById = async (slug) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      const deleteBlogApiPath = `${process.env.REACT_APP_API_URL}api/Blogs/${slug}`
      const token = localStorage.getItem("token");
      try {
        const res = await axios.delete(deleteBlogApiPath, {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        })
        setTimeout(async () => {
          await fetchBlogs(1)
          setCurrentPage(1)
          showSuccessToast("Blog deleted successfully")
        }, 1500);

      } catch (error) {
        showErrorToast(`Error deleting blog : ${error?.response?.data?.message || error.message}`);
        console.error("Error deleting blog:", error?.response?.data?.message || error.message);
        if (error.response?.status === 401) {
          showErrorToast("Unauthorized: Please log in again.");
        }
      }
    }
  }

  const fetchBlogs = async (pageNumber = 1, isInitialLoad = false) => {
    try {
      // Use URLSearchParams to manage query params
      const params = new URLSearchParams({
        page: pageNumber,
        pageSize: blogsPerPage,
      });

      if (filterStatus && filterStatus.trim() !== "") {
        params.append("status", filterStatus);
      }
      if (searchTerm && searchTerm.trim() !== "") {
        params.append("search", searchTerm.trim());
      }

      const getBlogPath = `${process.env.REACT_APP_API_URL}api/Blogs?${params.toString()}`;

      const token = localStorage.getItem("token");

      const res = await axios.get(getBlogPath, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = res.data || { blogs: [], totalCount: 0 };
      setBlogs(data.blogs)
      setTotalBlogs(data.totalCount)

    } catch (error) {
      showErrorToast(`Failed to fetch blogs : ${error?.response?.data?.message || error.message}`);
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setLoading(true);

    try {
      const featureImageBase64 = formData.featureImageUrl

      const thumbnailImageBase64 = formData.thumbnailImageUrl



      if (!featureImageBase64 || !thumbnailImageBase64) {
        return showErrorToast("Thumbnail and feature both image url is required");
      }

      // Prepare payload in the structure backend expects
      const payload = {
        title: formData.title,
        slug: formData.slug,
        htmlContent: formData.htmlContent,
        shortDesc: formData.shortDesc,
        metaDescription: formData.metaDescription,
        metaKeywords: formData.metaKeywords,
        blogStatus: Number.isInteger(Number(formData.blogStatus))
          ? Number(formData.blogStatus)
          : 0,
        featureImageBase64,
        thumbnailImageBase64,
        featureImageAlt: formData.featureImageAlt || '',
        thumbnailImageAlt: formData.thumbnailImageAlt || '',
      };

      if (editingBlog) {
        const updatedBlog = {
          ...payload,
          id: formData.id,
          lastModificationTime: new Date().toISOString(),
          lastModifierUserName: 'Admin User',

        };

        await handleEditBlog(updatedBlog);

      } else {
        const newBlog = {
          ...payload,
          id: Date.now(),
          creationTime: new Date().toISOString(),
          blogViewed: 0,
        };

        await handleCreateBlog(newBlog);

      }
      setTimeout(async () => {
        resetForm();
        setShowModal(false);
        setLoading(false);
        setIsUploading(false);
        await fetchBlogs(1);
        const successMessage = `Blog ${editingBlog ? "updated" : "created"} successfully!`;
        showSuccessToast(successMessage);
      }, 5000);

    } catch (error) {
      setIsUploading(false);
      setLoading(false);
      console.error('Error saving blog:', error);
    }
    // finally {
    //   setLoading(false);
    // }
  };


  const resetForm = () => {
    setFormData({
      id: 0,
      title: '',
      slug: '',
      htmlContent: '',
      shortDesc: '',
      metaDescription: '',
      metaKeywords: '',
      blogStatus: 0,
      thumbnailImageUrl: '',
      featureImageUrl: '',
      featureImageAlt: '',
      thumbnailImageAlt: '',
      creatorUserName: 'Admin User'
    });
    setErrors({});
    setEditingBlog(null);
    setThumbnailPreview('');
    setFeaturePreview('');
  };


  const handleChangeStatus = async (slug, status) => {
    setOpenDropdownId(null); // Close the dropdown after selection
    const publishStatusPath = `${process.env.REACT_APP_API_URL}api/Blogs/${slug}/status`;
    const token = localStorage.getItem("token");

    try {
      const res = await axios.patch(
        publishStatusPath,
        status, // <-- Empty body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setTimeout(async () => {
        await fetchBlogs(currentPage)
        showSuccessToast("Status changed successfully")
      }, 1500);
      
    } catch (error) {
      console.error("Error increasing view count:", error);
      showErrorToast(`Error while changing status : ${error?.response?.data?.message || error.message}`);

      if (error.response && error.response.status === 401) {
        showErrorToast("Unauthorized: Please log in again.");
      }
    }
  }
  const handleThumbnailChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const sizeKB = file.size / 1024;

      if (sizeKB > 200) {
        showErrorToast('Thumbnail image must be less than 200KB');
        return;
      }
      const thumbnailBase64 = await fileToBase64(file);
      setFormData((prev) => ({
        ...prev,
        thumbnailImageUrl: thumbnailBase64
      }));

      const reader = new FileReader();
      reader.onload = (event) => {
        setThumbnailPreview(event.target.result);
      };
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        showErrorToast("Failed to read image. Please try again.");
      };
      reader.readAsDataURL(file);
    }
  };
  const handleFeatureImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const sizeKB = file.size / 1024;

      if (sizeKB > 500) {
        showErrorToast('Feature image must be less than 500KB');
        return;
      }
      const featureBase64 = await fileToBase64(file);
      // setFeatureFile(file);
      setFormData((prev) => ({
        ...prev,
        featureImageUrl: featureBase64
      }));
      const reader = new FileReader();
      reader.onload = (event) => {
        setFeaturePreview(event.target.result);
      };
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        showErrorToast("Failed to read image. Please try again.");
      };

      reader.readAsDataURL(file);
    }
  };


  useEffect(() => {
    fetchBlogs(currentPage)
  }, [currentPage, filterStatus, searchTerm]);


  const indexOfLastProperty = currentPage * blogsPerPage;
  const indexOfFirstProperty = indexOfLastProperty - blogsPerPage;

  const paginate = (pageNumber) => {
    if (pageNumber !== currentPage && !isUploading) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col">
          <h1 className="h3 mb-0">Blog Management</h1>
          <p className="text-muted">Manage your blog posts and content</p>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            New Blog Post
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">🔍</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); }}
          >
            <option value="">All Status</option>
            <option value="0">Draft</option>
            <option value="1">Published</option>
            <option value="2">Archived</option>
          </select>
        </div>
        <div className="col-md-3">
          <div className="text-end">
            <small className="text-muted">
              Showing {blogs?.length} of {blogs?.length} blogs
            </small>
          </div>
        </div>
      </div>

      {/* Blog List */}
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Views</th>
                      <th>Author</th>
                      <th>Created</th>
                      <th width="150">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs?.map(blog => (
                      <tr key={blog.id}>
                        <td>
                          <div>
                            <div className="fw-semibold">{blog.title}</div>
                            <small className="text-muted">{blog.shortDesc}</small>
                          </div>
                        </td>

                        <td>{getStatusBadge(blog.blogStatus)}</td>
                        <td><span className="badge bg-info">{blog.blogViewed || 0}</span></td>
                        <td>{blog.creatorUserName}</td>
                        <td>
                          <small className="text-muted">
                            {new Date(blog.creationTime).toLocaleDateString()}
                          </small>
                        </td>

                        <td>
                          <div className="btn-group btn-group-sm" style={{ position: "relative" }}>
                            {/* Edit */}
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => {
                                getBlogById(blog);
                              }}
                              title="Edit"
                            >
                              ✏️
                            </button>

                            {/* Dropdown */}
                            <div className="btn-group btn-group-sm" style={{ position: "relative" }}>
                              <button
                                className="btn btn-outline-secondary dropdown-toggle"
                                type="button"
                                title="Change Status"
                                onClick={() =>
                                  setOpenDropdownId(openDropdownId === blog.id ? null : blog.id)
                                }
                              >
                                ⚙️
                              </button>

                              {openDropdownId === blog.id && (
                                <ul
                                  className='d-flex flex-column gap-2 dropdown-menu show shadow'
                                  style={{
                                    display: "block",
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    zIndex: 100000,
                                    width: "200px",
                                    padding: "0.5rem 0",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <li>
                                    <button
                                      className="dropdown-item dropdown-item fw-semibold py-2"
                                      onClick={() => handleChangeStatus(blog.slug, 0)}
                                    >
                                      📄 Draft
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      className="dropdown-item dropdown-item fw-semibold py-2"
                                      onClick={() => handleChangeStatus(blog.slug, 1)}
                                    >
                                      ✅ Publish
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      className="dropdown-item dropdown-item fw-semibold py-2"
                                      onClick={() => handleChangeStatus(blog.slug, 2)}
                                    >
                                      🗄️ Archive
                                    </button>
                                  </li>
                                </ul>
                              )}
                            </div>

                            {/* Delete */}
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => {
                                // handleDelete(blog.id); 
                                deleteBlogById(blog.slug);
                              }}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>

              {blogs?.length === 0 && (
                <div className="text-center py-5">
                  <div className="text-muted">
                    <h5>No blogs found</h5>
                    <p>Create your first blog post to get started.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Pagination */}
      {totalBlogs > 0 && (
        <nav className="d-flex justify-content-center mt-4">
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                disabled={currentPage === 1 || isUploading}
              >
                Prev
              </button>
            </li>

            {Array.from({ length: Math.ceil(totalBlogs / blogsPerPage) }).map((_, index) => {
              const pageNumber = index + 1;
              const isCurrentPage = pageNumber === currentPage;

              const shouldShowPage =
                pageNumber === 1 ||
                pageNumber === Math.ceil(totalBlogs / blogsPerPage) ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

              if (shouldShowPage) {
                return (
                  <li key={pageNumber} className={`page-item ${isCurrentPage ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => paginate(pageNumber)}
                      disabled={isUploading}
                    >
                      {pageNumber}
                    </button>
                  </li>
                );
              }

              return null;
            })}

            <li className={`page-item ${currentPage === Math.ceil(totalBlogs / blogsPerPage) ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => paginate(
                  currentPage < Math.ceil(totalBlogs / blogsPerPage)
                    ? currentPage + 1
                    : Math.ceil(totalBlogs / blogsPerPage)
                )}
                disabled={currentPage === Math.ceil(totalBlogs / blogsPerPage) || isUploading}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
      {/* Blog Form Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingBlog ? '✏️ Edit Blog Post' : '➕ Create New Blog Post'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="row g-3">
                  {/* Basic Information */}
                  <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3">📝 Basic Information</h6>
                  </div>

                  <div className="col-md-8">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter blog title"
                    />
                    {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      name="blogStatus"
                      value={formData.blogStatus}
                      onChange={handleInputChange}
                    >
                      <option value={0}>📄 Draft</option>
                      <option value={1}>✅ Published</option>
                      <option value={2}>🗄️ Archived</option>
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Slug *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.slug ? 'is-invalid' : ''}`}
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="url-friendly-slug"
                    />
                    <div className="form-text">URL: /blog/{formData.slug}</div>
                    {errors.slug && <div className="invalid-feedback">{errors.slug}</div>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Short Description *</label>
                    <textarea
                      className={`form-control ${errors.shortDesc ? 'is-invalid' : ''}`}
                      name="shortDesc"
                      rows="2"
                      value={formData.shortDesc}
                      onChange={handleInputChange}
                      placeholder="Brief description of the blog post"
                      maxLength="200"
                    />
                    <div className="form-text">
                      {formData.shortDesc?.length}/200 characters
                    </div>
                    {errors.shortDesc && <div className="invalid-feedback">{errors.shortDesc}</div>}
                  </div>



                  {/* SEO Information */}
                  <div className="col-12 mt-4">
                    <h6 className="border-bottom pb-2 mb-3">🔍 SEO Information</h6>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Meta Description</label>
                    <textarea
                      className={`form-control ${errors.metaDescription ? 'is-invalid' : ''}`}
                      name="metaDescription"
                      rows="2"
                      value={formData.metaDescription}
                      onChange={handleInputChange}
                      placeholder="Brief description for search engines"
                      maxLength="160"
                    />
                    <div className="form-text">
                      {formData.metaDescription?.length}/160 characters - Optimal length: 120-160 chars
                    </div>
                    {errors.metaDescription && <div className="invalid-feedback">{errors.metaDescription}</div>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Meta Keywords</label>
                    <input
                      type="text"
                      className="form-control"
                      name="metaKeywords"
                      value={formData.metaKeywords}
                      onChange={handleInputChange}
                      placeholder="react, javascript, web development, tutorial"
                      maxLength="255"
                    />
                    <div className="form-text">
                      Separate keywords with commas. 5-10 keywords recommended.
                    </div>
                  </div>

                  {/* Image Information */}
                  <div className="col-12 mt-4">
                    <h6 className="border-bottom pb-2 mb-3">🖼️ Images</h6>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Feature Image</label>

                    <div className="mt-1">
                      <div className="border border-2 border-dashed rounded p-3 text-center">
                        {featurePreview ? (
                          <div className="position-relative" style={{ height: "200px" }}>
                            <img
                              src={featurePreview}
                              alt="Thumbnail preview"
                              className="img-fluid h-100 object-fit-cover rounded"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setFeaturePreview(null);
                                // setFeatureFile(null);
                                // deleteThumbNail();
                                setFormData({
                                  ...formData,
                                  featureImageUrl: '',
                                });
                              }}
                              className="position-absolute top-0 end-0 btn btn-sm btn-light rounded-circle m-2"
                              disabled={isUploading}
                            >
                              <X size={16} className="text-danger" />
                            </button>
                          </div>
                        ) : (
                          <label className="d-flex flex-column align-items-center p-4 cursor-pointer">
                            <Upload size={40} className="text-secondary mb-2" />
                            <span className="text-secondary">
                              Click to upload feature image
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFeatureImageChange}
                              className="d-none"
                              disabled={isUploading}
                            />
                          </label>
                        )}
                        {errors.featureImageUrl && <div className={`form-control ${errors.featureImageUrl ? 'is-invalid' : ''}`}>{errors.featureImageUrl}</div>}

                      </div>
                    </div>

                    <div className="form-text">Main image displayed with the blog post</div>
                  </div>


                  <div className="col-md-6">
                    <label className="form-label">Feature Image Alt Text</label>
                    <input
                      type="text"
                      className="form-control"
                      name="featureImageAlt"
                      value={formData.featureImageAlt}
                      onChange={handleInputChange}
                      placeholder="Descriptive text for screen readers"
                      maxLength="200"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Thumbnail Image</label>

                    <div className="mt-1">
                      <div className="border border-2 border-dashed rounded p-3 text-center">
                        {thumbnailPreview ? (
                          <div className="position-relative" style={{ height: "200px" }}>
                            <img
                              src={thumbnailPreview}
                              alt="Thumbnail preview"
                              className="img-fluid h-100 object-fit-cover rounded"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setThumbnailPreview(null);
                                setFormData({
                                  ...formData,
                                  thumbnailImageUrl: '',
                                });
                              }}
                              className="position-absolute top-0 end-0 btn btn-sm btn-light rounded-circle m-2"
                              disabled={isUploading}
                            >
                              <X size={16} className="text-danger" />
                            </button>
                          </div>
                        ) : (
                          <label className="d-flex flex-column align-items-center p-4 cursor-pointer">
                            <Upload size={40} className="text-secondary mb-2" />
                            <span className="text-secondary">
                              Click to upload thumbnail
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleThumbnailChange}
                              className="d-none"
                              disabled={isUploading}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                    {errors.thumbnailImageUrl
                      && <div className={`form-control ${errors.thumbnailImageUrl ? 'is-invalid' : ''}`}>{errors.thumbnailImageUrl
                      }</div>}
                    <div className="form-text">Smaller image for blog listings</div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Thumbnail Image Alt Text</label>
                    <input
                      type="text"
                      className="form-control"
                      name="thumbnailImageAlt"
                      value={formData.thumbnailImageAlt}
                      onChange={handleInputChange}
                      placeholder="Descriptive text for screen readers"
                      maxLength="200"
                    />
                  </div>



                  <div className="col-12" style={{ position: 'relative' }}>
                    <label className="form-label">Content *</label>
                    <ReactQuill
                      value={formData.htmlContent}
                      theme="snow"
                      // onChange={setValue}
                      onChange={(value) => handleInputChange({ target: { name: 'htmlContent', value } })}
                      modules={{ toolbar: toolbarOptions }}
                      placeholder="Start writing..."
                      style={{ height: '400px', marginBottom: '3rem' }} // container height
                      className="quill-editor"
                    />

                    {errors.htmlContent && <div className={`form-control ${errors.thumbnailImageUrl ? 'is-invalid' : ''}`}>{errors.htmlContent}</div>}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowModal(false); resetForm(); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {editingBlog ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingBlog ? '💾 Update Blog' : '✨ Create Blog'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;